import cx from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChartBarDecreasing,
  ChartBarIncreasing,
  ChevronLeft,
  Search as SearchIcon,
  X as XIcon,
} from "lucide-react";
import { Popup } from "reactjs-popup";
import ArrowUpCircleIcon from "~/assets/svg/ArrowUpCircle";
import FilterPlusIcon from "~/assets/svg/FilterPlus";

export default function ProductActionBar({
  service,
  goBack,
  textQuery,
  setTextQuery,
  sortFilter,
  setSortFilter,
  i18n,
  t,
}) {
  return (
    <div className="relative w-full flex gap-2 items-center">
      <button
        className="flex items-center justify-center text-center leading-loose"
        onClick={goBack}
        aria-label={t("goBack")}
      >
        <ChevronLeft
          className={cx({
            "rotate-180": i18n.dir() === "rtl",
          })}
        />
      </button>
      <div
        style={{
          backgroundImage: `url(https://s3.hidsim.com/services/${service.name.a2}.svg)`,
        }}
        className="h-8 w-8 md:h-10 md:w-10 rounded-full image-full bg-center bg-cover block border border-base-300/70"
      />
      <div className="relative grow rounded-full bg-base-200 flex gap-1 items-center py-3 md:py-4">
        <SearchIcon className="text-black ms-4" strokeWidth={2} />
        <input
          type="text"
          placeholder={t("searchServices")}
          className="shrink-0 grow basis-0 items-center rounded-full bg-base-200 px-1 focus:outline-none w-full leading-none"
          onChange={(e) => setTextQuery(e.target.value)}
          value={textQuery}
        />
        {textQuery && (
          <button
            onClick={() => setTextQuery("")}
            className={cx(
              "absolute top-4.5 flex h-5 w-5 items-center justify-center text-center text-3xl leading-none text-neutral-500",
              {
                "right-4": i18n.dir() === "ltr",
                "left-4": i18n.dir() === "rtl",
              },
            )}
            aria-label={t("clearSearch")}
          >
            <XIcon />
          </button>
        )}
      </div>
      <AnimatePresence>
        <Popup
          trigger={
            <button aria-label={t("filter")}>
              <label className="rounded-btn cursor-pointer">
                <FilterPlusIcon className="h-6 w-6" />
              </label>
            </button>
          }
          position={i18n.dir() === "ltr" ? "bottom right" : "bottom left"}
          arrow={false}
          contentStyle={{
            padding: 0,
            border: 0,
            backgroundColor: "transparent",
            boxShadow: "none",
          }}
          repositionOnResize
        >
          <motion.ul
            className="menu rounded-box z-[1] mt-4 w-52 border border-neutral-500 border-opacity-20 bg-base-100 p-2 shadow-2xl flex flex-col gap-1"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <motion.li
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2, delay: 0 }}
            >
              <button
                className={cx("flex justify-between", {
                  active: sortFilter === "default",
                })}
                onClick={() => {
                  setSortFilter("default");
                  close();
                }}
                aria-label={t("filter")}
              >
                {t("default")}
                <FilterPlusIcon className="h-5 w-5" />
              </button>
            </motion.li>
            <motion.li
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2, delay: 0.1 }}
            >
              <button
                className={cx("flex justify-between", {
                  active: sortFilter === "price-low-to-high",
                })}
                onClick={() => {
                  setSortFilter("price-low-to-high");
                  close();
                }}
              >
                {t("priceLowToHigh")}
                <ArrowUpCircleIcon className="h-5 w-5" />
              </button>
            </motion.li>
            <motion.li
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2, delay: 0.2 }}
            >
              <button
                className={cx("flex justify-between", {
                  active: sortFilter === "price-high-to-low",
                })}
                onClick={() => {
                  setSortFilter("price-high-to-low");
                  close();
                }}
              >
                {t("priceHighToLow")}
                <ArrowUpCircleIcon className="h-5 w-5 rotate-180" />
              </button>
            </motion.li>
            <motion.li
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2, delay: 0.3 }}
            >
              <button
                className={cx("flex justify-between", {
                  active: sortFilter === "quality-high-to-low",
                })}
                onClick={() => {
                  setSortFilter("quality-high-to-low");
                  close();
                }}
              >
                {t("qualityHighToLow")}
                <ChartBarDecreasing className="h-5 w-5" />
              </button>
            </motion.li>
            <motion.li
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2, delay: 0.4 }}
            >
              <button
                className={cx("flex justify-between", {
                  active: sortFilter === "quality-low-to-high",
                })}
                onClick={() => {
                  setSortFilter("quality-low-to-high");
                  close();
                }}
              >
                {t("qualityLowToHigh")}
                <ChartBarIncreasing className="h-5 w-5" />
              </button>
            </motion.li>
          </motion.ul>
        </Popup>
      </AnimatePresence>
    </div>
  );
}
